/**
 * SignaturePad — pavé de signature manuscrite (M1, retour Manon).
 *
 * REPRIS TEL QUEL de Mettron (`apps/web/src/screens/SignaturePad.tsx`), à la
 * demande d'Henrico : même tracé lissé, même undo, mêmes précautions mobile.
 * Seule adaptation : les couleurs des boutons passent sur les jetons de thème
 * de VPC (clair et sombre) au lieu des teintes sombres codées en dur de Mettron.
 * Le cadre de signature reste BLANC : encre foncée lisible, impression propre.
 *
 * Notes d'origine (Mettron) :
 * Utilisé aux DEUX endroits : sur place (écran du pro) et à distance (page publique
 * du client). Même composant, donc même comportement et un seul endroit à corriger.
 *
 * Pourquoi pas le canvas du croquis (SketchModal) : il gère formes, textes, photo de
 * fond, gomme, sélection… Une signature n'a besoin que d'un tracé lissé et d'un undo.
 * Le détourner aurait apporté 700 lignes de complexité pour rien.
 *
 * Points d'attention mobile :
 *  • `touch-action: none` — sans ça, le doigt fait défiler la page au lieu d'écrire.
 *  • `setPointerCapture` — le tracé continue même si le doigt sort du cadre.
 *  • canvas en résolution écran (devicePixelRatio) — sinon le trait est flou.
 */

import { useEffect, useRef, useState } from 'react';

interface Pt { x: number; y: number }
type Trait = Pt[];

export function SignaturePad({
  onChange, height = 150, disabled = false,
}: {
  /** Appelé à chaque modification : PNG data-URL, ou null si le pavé est vide. */
  onChange: (dataUrl: string | null) => void;
  height?: number;
  disabled?: boolean;
}) {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const [traits, setTraits] = useState<Trait[]>([]);
  const drawing = useRef(false);
  const cur = useRef<Trait>([]);

  /** Coordonnées dans le repère du canvas (pas de la page). */
  function toCanvas(e: React.PointerEvent): Pt {
    const r = (e.currentTarget as HTMLCanvasElement).getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  /** Redessine tout. Tracé lissé (quadratique) : sinon la signature fait « robot ». */
  function redraw(all: Trait[]) {
    const cv = cvRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cv.width / dpr, cv.height / dpr);
    ctx.strokeStyle = '#12203a';
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const t of all) {
      if (t.length < 2) {
        // Un simple point (le client a juste tapoté) : on trace un rond.
        if (t[0]) { ctx.beginPath(); ctx.arc(t[0].x, t[0].y, 1.2, 0, Math.PI * 2); ctx.fillStyle = '#12203a'; ctx.fill(); }
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(t[0]!.x, t[0]!.y);
      for (let i = 1; i < t.length - 1; i++) {
        const a = t[i]!, b = t[i + 1]!;
        ctx.quadraticCurveTo(a.x, a.y, (a.x + b.x) / 2, (a.y + b.y) / 2);
      }
      ctx.lineTo(t[t.length - 1]!.x, t[t.length - 1]!.y);
      ctx.stroke();
    }
  }

  /** Le canvas doit suivre la largeur réelle ET la densité de l'écran. */
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = cv.clientWidth;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(height * dpr);
      redraw(traits);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
    // NB : pas de `eslint-disable react-hooks/exhaustive-deps` ici — le plugin
    // react-hooks n'est pas enregistré dans eslint.config.js, et ESLint 9 lève
    // une ERREUR quand on désactive une règle qu'il ne connaît pas (ce qui
    // rendait la CI rouge). Les dépendances ci-dessous sont de toute façon
    // exactes : `redraw` est une fonction de portée composant, pas une dep.
  }, [height, traits]);

  function emit(all: Trait[]) {
    const cv = cvRef.current;
    if (!cv) return;
    onChange(all.length === 0 ? null : cv.toDataURL('image/png'));
  }

  function down(e: React.PointerEvent) {
    if (disabled) return;
    try { (e.target as Element).setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    drawing.current = true;
    cur.current = [toCanvas(e)];
  }
  function move(e: React.PointerEvent) {
    if (!drawing.current || disabled) return;
    cur.current.push(toCanvas(e));
    redraw([...traits, cur.current]);
  }
  function up() {
    if (!drawing.current) return;
    drawing.current = false;
    if (cur.current.length === 0) return;
    const all = [...traits, cur.current];
    cur.current = [];
    setTraits(all);
    redraw(all);
    emit(all);
  }

  function undo() {
    const all = traits.slice(0, -1);
    setTraits(all); redraw(all); emit(all);
  }
  function clear() {
    setTraits([]); redraw([]); emit([]);
  }

  return (
    <div className="sp-wrap">
      <style>{SP_CSS}</style>
      <div className="sp-pad">
        <canvas
          ref={cvRef}
          style={{ height }}
          onPointerDown={down} onPointerMove={move}
          onPointerUp={up} onPointerCancel={up} onPointerLeave={up}
        />
        <div className="sp-line" />
        {traits.length === 0 && <div className="sp-hint">Signez ici avec votre doigt</div>}
      </div>
      <div className="sp-act">
        <button type="button" onClick={clear} disabled={disabled || !traits.length}>↺ Effacer</button>
        <button type="button" onClick={undo} disabled={disabled || !traits.length}>↶ Annuler le trait</button>
      </div>
    </div>
  );
}

const SP_CSS = `
.sp-wrap{width:100%}
.sp-pad{position:relative;background:#fff;border-radius:10px;overflow:hidden;margin-bottom:9px;border:1px solid #cbd5e1}
/* touch-action:none — sans ça le doigt fait défiler la page au lieu d'écrire. */
.sp-pad canvas{display:block;width:100%;touch-action:none;cursor:crosshair}
.sp-line{position:absolute;left:16px;right:16px;bottom:34px;border-bottom:1px dashed #9aa;pointer-events:none}
.sp-hint{position:absolute;left:16px;bottom:14px;font-size:10.5px;color:#8a94a6;pointer-events:none}
.sp-act{display:flex;gap:8px}
.sp-act button{flex:1;background:none;border:1px solid var(--c-border, #cbd5e1);color:inherit;border-radius:9px;padding:9px;
  font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;opacity:.85}
.sp-act button:hover:not(:disabled){opacity:1}
.sp-act button:disabled{opacity:.4;cursor:default}
`;
